(() => {
  // Every cx-* section renders the script tag; only the first copy runs.
  if (window.CXLoaded) return;
  window.CXLoaded = true;
/* CreaStix sections runtime (cx-*). Loaded with defer by snippets/cx-core.liquid, only on pages that render
   those sections. One scroll loop drives every pinned scene; scenes register with CX.addScene({ measure, update }).
   Section modules queue their setup on window.CXQ and run once this file has loaded. */
(() => {
  if (window.CX) return;

  const root = document.documentElement;
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const lerp = (a, b, t) => a + (b - a) * t;
  const seg = (p, a, b) => (b === a ? (p >= b ? 1 : 0) : clamp((p - a) / (b - a)));
  const ease = {
    linear: (t) => t,
    in: (t) => t * t * t,
    out: (t) => 1 - (1 - t) ** 3,
    inOut: (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2),
  };
  const copy = (value) => (Array.isArray(value) ? value.slice() : value);

  // Keyframes: [[t, number | number[]], ...] in ascending t. Holds the first/last value outside the range.
  const kf = (p, frames, curve = ease.inOut) => {
    if (p <= frames[0][0]) return copy(frames[0][1]);
    for (let i = 1; i < frames.length; i += 1) {
      const [t1, v1] = frames[i];
      if (p <= t1) {
        const [t0, v0] = frames[i - 1];
        const t = curve(t1 === t0 ? 1 : (p - t0) / (t1 - t0));
        return Array.isArray(v0) ? v0.map((v, k) => lerp(v, v1[k], t)) : lerp(v0, v1, t);
      }
    }
    return copy(frames[frames.length - 1][1]);
  };

  // Writes transform/opacity only when they change.
  const set = (el, transform, opacity) => {
    if (!el) return;
    if (transform !== undefined && el.cxT !== transform) {
      el.cxT = transform;
      el.style.transform = transform;
    }
    if (opacity !== undefined) {
      const o = Math.round(clamp(opacity) * 1000) / 1000;
      if (el.cxO !== o) {
        el.cxO = o;
        el.style.opacity = String(o);
      }
    }
  };

  const docTop = (el) => el.getBoundingClientRect().top + window.scrollY;

  /* ---------- Scenes ---------- */
  const scenes = new Set();
  let view = { y: window.scrollY, h: window.innerHeight, w: window.innerWidth };
  let scrollFrame = 0;
  let measureFrame = 0;
  const readView = () => {
    view = { y: window.scrollY, h: window.innerHeight, w: window.innerWidth };
    return view;
  };
  const inOrder = (a, b) => {
    const ea = a.section || a.el;
    const eb = b.section || b.el;
    if (!ea || !eb || ea === eb) return 0;
    return ea.compareDocumentPosition(eb) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  };
  const run = (fn) => {
    try {
      fn();
    } catch (error) {
      console.error(error);
    }
  };
  const updateAll = () => scenes.forEach((scene) => run(() => scene.update(view)));
  // Top to bottom: a scene's measure can change the page height below it, never above it.
  const measureAll = () => {
    measureFrame = 0;
    readView();
    Array.from(scenes).sort(inOrder).forEach((scene) => run(() => scene.measure(view)));
    updateAll();
  };
  const remeasure = () => {
    if (!measureFrame) measureFrame = window.requestAnimationFrame(measureAll);
  };
  const onScroll = () => {
    if (scrollFrame) return;
    scrollFrame = window.requestAnimationFrame(() => {
      scrollFrame = 0;
      readView();
      updateAll();
    });
  };
  const addScene = (scene) => {
    scenes.add(scene);
    remeasure();
    return () => scenes.delete(scene);
  };

  let lastW = window.innerWidth;
  let lastH = window.innerHeight;
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    // Mobile toolbars change innerHeight while scrolling; stages use svh, so only re-measure for real resizes.
    const w = window.innerWidth;
    const h = window.innerHeight;
    if (w !== lastW || Math.abs(h - lastH) > 160) {
      lastW = w;
      lastH = h;
      remeasure();
    } else {
      onScroll();
    }
  });
  window.addEventListener('load', remeasure);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(remeasure);
  if ('ResizeObserver' in window) {
    let lastBody = 0;
    new ResizeObserver(() => {
      const hgt = document.body.offsetHeight;
      if (Math.abs(hgt - lastBody) > 2) {
        lastBody = hgt;
        remeasure();
      }
    }).observe(document.body);
  }

  /* ---------- Tickers (time-based loops, e.g. the powder) ---------- */
  const tickers = new Set();
  let tickFrame = 0;
  const tick = (now) => {
    tickFrame = 0;
    tickers.forEach((fn) => {
      let keep = false;
      run(() => {
        keep = fn(now);
      });
      if (!keep) tickers.delete(fn);
    });
    if (tickers.size) tickFrame = window.requestAnimationFrame(tick);
  };
  const addTicker = (fn) => {
    tickers.add(fn);
    if (!tickFrame) tickFrame = window.requestAnimationFrame(tick);
    return () => tickers.delete(fn);
  };

  /* ---------- Image pre-decode for pinned stages ---------- */
  const predecode = (section, rootMargin = '150% 0px 150% 0px') => {
    if (!section || !('IntersectionObserver' in window)) return () => {};
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        section.querySelectorAll('img').forEach((img) => {
          if (img.loading === 'lazy') img.loading = 'eager';
          if (img.decode) img.decode().catch(() => {});
        });
        io.disconnect();
      });
    }, { rootMargin });
    io.observe(section);
    return () => io.disconnect();
  };

  /* ---------- Reduced motion ---------- */
  const reduceHandlers = new Set();
  const onReduceChange = (fn) => reduceHandlers.add(fn);
  const onMotionChange = () => {
    CX.reduced = motionQuery.matches;
    reduceHandlers.forEach((fn) => run(fn));
    remeasure();
  };
  if (motionQuery.addEventListener) motionQuery.addEventListener('change', onMotionChange);
  else if (motionQuery.addListener) motionQuery.addListener(onMotionChange);

  /* ---------- Entrance reveals ---------- */
  let revealIO = null;
  const scanReveals = (scope = document) => {
    const items = scope.querySelectorAll('[data-cx-reveal]:not(.is-in)');
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    if (!revealIO) {
      revealIO = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          revealIO.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    }
    items.forEach((el) => revealIO.observe(el));
  };

  /* ---------- Rolling digits (offer clocks, counters) ---------- */
  const ROLL_MS = 380;
  const rollDigit = (el, value, animate = true, dir = 1) => {
    if (!el) return;
    const v = String(value);
    if (el.dataset.v === v) return;
    el.dataset.v = v;
    const [current, incoming] = el.children;
    if (!current) return;
    window.clearTimeout(el.cxRoll);
    if (!animate || CX.reduced || !incoming) {
      current.textContent = v;
      current.style.transition = '';
      current.style.transform = '';
      if (incoming) {
        incoming.textContent = '';
        incoming.style.transition = '';
        incoming.style.transform = '';
      }
      return;
    }
    const from = dir > 0 ? -104 : 104;
    incoming.textContent = v;
    incoming.style.transition = 'none';
    current.style.transition = 'none';
    incoming.style.transform = `translate3d(0, ${from}%, 0)`;
    current.style.transform = 'translate3d(0, 0, 0)';
    incoming.getBoundingClientRect();
    const curve = `transform ${ROLL_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1)`;
    incoming.style.transition = curve;
    current.style.transition = curve;
    incoming.style.transform = 'translate3d(0, 0, 0)';
    current.style.transform = `translate3d(0, ${-from}%, 0)`;
    el.cxRoll = window.setTimeout(() => {
      current.style.transition = 'none';
      current.textContent = v;
      current.style.transform = '';
      incoming.style.transition = 'none';
      incoming.style.transform = '';
      incoming.textContent = '';
    }, ROLL_MS + 30);
  };

  const pad = (n) => String(n).padStart(2, '0');
  let lastOfferState = root.dataset.offer;
  document.addEventListener('creastix:offer', (event) => {
    const detail = event.detail || {};
    const total = Math.ceil(Math.max(0, detail.remaining || 0) / 1000);
    const text = `${pad(Math.floor(total / 60))}${pad(total % 60)}`;
    document.querySelectorAll('[data-cx-clock]').forEach((clock) => {
      clock.querySelectorAll('[data-cx-dg]').forEach((digit, i) => rollDigit(digit, text[i], true, 1));
    });
    if (lastOfferState === 'active' && detail.state === 'ended') root.classList.add('cx-offer-just-ended');
    lastOfferState = detail.state;
  });

  /* ---------- Powder: falling grains on a canvas (the pour) ---------- */
  class Powder {
    constructor(canvas, CX) {
      this.CX = CX;
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.setColors('#ffffff');
      this.count = 0;
      this.rate = 0;
      this.origin = null;
      this.dir = [0, 1];
      this.spread = 0;
      this.running = false;
      this.last = 0;
      this.tick = this.tick.bind(this);
    }

    // Keeps grains already in the air in their old color; new grains use the new palette.
    setColors(list) {
      const colors = String(list).split(',').map((c) => c.trim()).filter(Boolean);
      this.colors = colors.length ? colors : ['#ffffff'];
      this.palette = (this.palette || 0) + 1;
    }

    resize(w, h) {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.w = w;
      this.h = h;
      this.canvas.width = Math.round(w * dpr);
      this.canvas.height = Math.round(h * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.grains = [];
      this.count = 0;
    }

    set(rate, origin, dir, spread) {
      this.rate = rate;
      this.origin = origin;
      this.dir = dir;
      this.spread = spread;
      if ((rate > 0 || this.count > 0) && !this.running) {
        this.running = true;
        this.last = performance.now();
        this.CX.addTicker(this.tick);
      }
    }

    step(dt) {
      const g = this.h * 2.3;
      if (!this.grains) this.grains = [];
      if (this.rate > 0 && this.origin) {
        this.carry = (this.carry || 0) + this.rate * dt;
        const n = Math.floor(this.carry);
        this.carry -= n;
        const [dx, dy] = this.dir;
        for (let i = 0; i < n; i += 1) {
          const across = (Math.random() + Math.random() - 1) * this.spread;
          const speed = this.h * (0.05 + Math.random() * 0.1);
          this.grains.push({
            x: this.origin[0] - dy * across,
            y: this.origin[1] + dx * across,
            vx: dx * speed + (Math.random() - 0.5) * this.h * 0.02,
            vy: dy * speed + (Math.random() - 0.5) * this.h * 0.02,
            s: 0.8 + Math.random() * Math.random() * 1.9,
            c: this.colors[(Math.random() * this.colors.length) | 0],
          });
        }
      }
      const limit = this.h + 10;
      const grains = this.grains;
      for (let i = grains.length - 1; i >= 0; i -= 1) {
        const q = grains[i];
        q.vy += g * dt;
        q.vx *= 0.985;
        q.x += q.vx * dt;
        q.y += q.vy * dt;
        if (q.y > limit) {
          grains[i] = grains[grains.length - 1];
          grains.pop();
        }
      }
      this.count = grains.length;
    }

    draw() {
      const { ctx } = this;
      ctx.clearRect(0, 0, this.w, this.h);
      // A faint ink edge under every grain keeps light powder readable over light photos and panels.
      if (this.edge) {
        ctx.fillStyle = this.edge;
        const grains = this.grains;
        for (let i = 0; i < grains.length; i += 1) ctx.fillRect(grains[i].x - 0.35, grains[i].y - 0.2, grains[i].s + 0.9, grains[i].s + 0.9);
      }
      ctx.globalAlpha = 0.92;
      // Batch by color so fillStyle changes stay few.
      const byColor = new Map();
      this.grains.forEach((q) => {
        let list = byColor.get(q.c);
        if (!list) byColor.set(q.c, (list = []));
        list.push(q);
      });
      byColor.forEach((list, color) => {
        ctx.fillStyle = color;
        for (let i = 0; i < list.length; i += 1) ctx.fillRect(list[i].x, list[i].y, list[i].s, list[i].s);
      });
      ctx.globalAlpha = 1;
    }

    tick(now) {
      const dt = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      this.step(dt);
      this.draw();
      if (this.rate > 0 || this.count > 0) return true;
      this.running = false;
      this.ctx.clearRect(0, 0, this.w, this.h);
      return false;
    }
  }


  const CX = {
    ready: false,
    reduced: motionQuery.matches,
    clamp,
    lerp,
    seg,
    ease,
    kf,
    set,
    docTop,
    addScene,
    remeasure,
    addTicker,
    predecode,
    onReduceChange,
    rollDigit,
    scanReveals,
    Powder,
    get view() {
      return view;
    },
  };
  window.CX = CX;

  const queue = window.CXQ || [];
  const flush = () => {
    CX.ready = true;
    root.classList.add('cx-ready');
    scanReveals();
    while (queue.length) {
      const fn = queue.shift();
      run(() => fn(CX));
    }
    // Later pushes (theme editor re-renders) run right away.
    window.CXQ = { push: (fn) => run(() => fn(CX)) };
    remeasure();
  };
  window.CXQ = queue;
  document.addEventListener('shopify:section:load', (event) => scanReveals(event.target));
  // Section modules below push onto CXQ synchronously; flush after they have all been queued.
  window.CXFlush = flush;
})();

/* ---------- Section modules ---------- */

/* ---------- offer ---------- */
(() => {
  /* ---------- sections/cx-offer.liquid: confirm a creator's code when it replaces our timer ---------- */
  (() => {
    const escape = (text) => String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
    const linkCode = () => {
      const match = document.cookie.match(/(?:^|; )discount_code=([^;]*)/);
      return match ? decodeURIComponent(match[1]) : '';
    };
    let cartCode = null;
    const fetchCartCode = () => {
      if (cartCode !== null) return Promise.resolve(cartCode);
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          const ours = window.CreastixOffer && window.CreastixOffer.code ? String(window.CreastixOffer.code).toLowerCase() : '';
          const codes = (cart.discount_codes || []).map((d) => d.code).filter((c) => c && c.toLowerCase() !== ours);
          cartCode = codes[0] || '';
          return cartCode;
        })
        .catch(() => '');
    };
    const show = () => {
      if (document.documentElement.dataset.offer !== 'other') return;
      document.querySelectorAll('[data-cx-offer-creator]').forEach((el) => {
        const paint = (code) => {
          const inner = el.firstElementChild;
          inner.innerHTML = code ? escape(el.dataset.text).replace('[code]', `<b>${escape(code.toUpperCase())}</b>`) : escape(el.dataset.fallback);
          el.hidden = false;
        };
        const code = linkCode();
        if (code) paint(code);
        else fetchCartCode().then(paint);
      });
    };
    (window.CXQ = window.CXQ || []).push(() => {
      show();
      document.addEventListener('creastix:offer', show);
      document.addEventListener('shopify:section:load', show);
    });
  })();
})();

/* ---------- hero ---------- */
(() => {
  (() => {
    const SELECTOR = '[data-cx-hero]';
    const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const initFlavors = (hero) => {
      const chips = Array.from(hero.querySelectorAll('[data-cx-pick]'));
      if (!chips.length) return;

      const keys = chips.map((chip) => chip.dataset.cxPick);
      let current = hero.dataset.flavor || keys[0];
      let locked = false;
      let hoverPause = false;
      let focusPause = false;
      let touchPauseUntil = 0;
      let inView = true;
      let timer;

      const apply = (key, source) => {
        if (!keys.includes(key)) return;
        current = key;
        hero.dataset.flavor = key;
        const chip = chips.find((item) => item.dataset.cxPick === key);
        if (chip && chip.dataset.cxAccent) hero.style.setProperty('--cx-accent', chip.dataset.cxAccent);

        hero.querySelectorAll('[data-cx-flavor]').forEach((el) => {
          const on = el.dataset.cxFlavor === key;
          if (el.hasAttribute('data-cx-swap')) el.hidden = !on;
          else el.classList.toggle('is-active', on);
        });
        chips.forEach((item) => item.setAttribute('aria-pressed', String(item.dataset.cxPick === key)));
        document.dispatchEvent(new CustomEvent('cx:flavor', { detail: { flavor: key, source } }));
      };

      const lock = () => {
        locked = true;
        window.clearInterval(timer);
      };

      chips.forEach((chip) => {
        chip.addEventListener('click', () => {
          lock();
          apply(chip.dataset.cxPick, 'user');
        });
      });

      document.addEventListener('cx:flavor-request', (event) => {
        if (!event.detail || !hero.isConnected) return;
        lock();
        apply(event.detail.flavor, 'user');
      });

      const seconds = Number(hero.dataset.cxAutoplay || 0);
      if (!seconds || prefersReducedMotion() || keys.length < 2) return;

      hero.querySelectorAll('[data-cx-hover-pause]').forEach((zone) => {
        zone.addEventListener('mouseenter', () => {
          hoverPause = true;
        });
        zone.addEventListener('mouseleave', () => {
          hoverPause = false;
        });
      });
      hero.addEventListener('focusin', () => {
        focusPause = true;
      });
      hero.addEventListener('focusout', (event) => {
        if (!hero.contains(event.relatedTarget)) focusPause = false;
      });
      hero.addEventListener('touchstart', () => {
        touchPauseUntil = Date.now() + 8000;
      }, { passive: true });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            inView = entry.isIntersecting;
          });
        }, { threshold: 0.35 }).observe(hero);
      }

      // The ritual below takes the stick over as soon as the page scrolls (is-handoff), so the
      // flavor holds still while it is in flight.
      const canPlay = () =>
        !locked &&
        !hoverPause &&
        !focusPause &&
        Date.now() > touchPauseUntil &&
        inView &&
        !document.hidden &&
        !hero.classList.contains('is-handoff');
      timer = window.setInterval(() => {
        if (!canPlay()) return;
        const next = keys[(keys.indexOf(current) + 1) % keys.length];
        apply(next, 'auto');
      }, Math.max(2, seconds) * 1000);
    };

    const init = (root) => {
      root.querySelectorAll(SELECTOR).forEach((hero) => {
        if (hero.dataset.cxReady === 'true') return;
        hero.dataset.cxReady = 'true';
        initFlavors(hero);
      });
    };

    (window.CXQ = window.CXQ || []).push(() => {
      init(document);
      document.addEventListener('shopify:section:load', (event) => init(event.target));
    });
  })();
})();

/* ---------- marquee ---------- */
(() => {
  /* ---------- sections/cx-marquee.liquid: drift, plus a push from the scroll ---------- */
  (() => {
    class Marquee {
      constructor(el, CX) {
        this.CX = CX;
        this.el = el;
        this.runs = Array.from(el.querySelectorAll('[data-cx-mq-run]')).map((run) => ({ run, dir: Number(run.dataset.dir) || 1, x: 0 }));
        this.boost = 0;
        this.lastY = window.scrollY;
        this.visible = false;
        this.tick = this.tick.bind(this);
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.visible = entry.isIntersecting;
            if (this.visible) this.start();
          });
        });
        this.io.observe(el);
        this.removeScene = CX.addScene(this);
      }

      destroy() {
        this.removeScene();
        this.io.disconnect();
        this.visible = false;
        this.runs.forEach(({ run }) => { run.style.transform = ''; });
      }

      measure() {
        this.runs.forEach((r) => {
          const set = r.run.firstElementChild;
          r.w = set ? set.offsetWidth : 1;
        });
      }

      // Scroll speed feeds a boost that decays; scrolling up reverses the drift.
      update(view) {
        const dy = view.y - this.lastY;
        this.lastY = view.y;
        this.boost = this.CX.clamp(this.boost + dy * 0.9, -900, 900);
      }

      start() {
        if (this.running) return;
        this.running = true;
        this.last = performance.now();
        this.CX.addTicker(this.tick);
      }

      tick(now) {
        const dt = Math.min(0.05, (now - this.last) / 1000);
        this.last = now;
        this.boost *= Math.pow(0.04, dt);
        const speed = 60 + Math.abs(this.boost);
        const sign = this.boost < -20 ? -1 : 1;
        this.runs.forEach((r) => {
          r.x = (r.x + r.dir * sign * speed * dt) % r.w;
          const x = r.dir < 0 ? r.x : r.x - r.w;
          this.CX.set(r.run, `translate3d(${x.toFixed(1)}px, 0, 0)`);
        });
        if (this.visible) return true;
        this.running = false;
        return false;
      }
    }

    const instances = new Map();
    const initAll = (CX, root = document) => {
      if (!('IntersectionObserver' in window)) return;
      root.querySelectorAll('[data-cx-marquee]').forEach((el) => {
        if (instances.has(el) || CX.reduced) return;
        instances.set(el, new Marquee(el, CX));
      });
    };
    const destroyAll = (root = document) => {
      instances.forEach((instance, el) => {
        if (root === document || root.contains(el)) {
          instance.destroy();
          instances.delete(el);
        }
      });
    };

    (window.CXQ = window.CXQ || []).push((CX) => {
      if (!document.querySelector('[data-cx-marquee]')) return;
      initAll(CX);
      CX.onReduceChange(() => {
        destroyAll();
        initAll(CX);
      });
      document.addEventListener('shopify:section:load', (event) => initAll(CX, event.target));
      document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
    });
  })();
})();

/* ---------- pour ---------- */
(() => {
  /* ---------- sections/cx-pour.liquid: tear, pour, then keep pouring into the flavor panels ---------- */
  (() => {
    const TEAR_Y = 0.062;
    // Scroll budget in stage heights (u). The tear and pour keep the pacing of the original scroll ritual.
    const U = {
      rest: 0.15,
      intro: [-5, -4.5, 0.45, 0.65],
      giant: [0.45, 0.75],
      tear: [0.7, 0.875, 1.3, 1.45],
      pourText: [1.5, 1.675, 2.12, 2.32],
      peel: [[0.875, 0], [1.05, 9], [1.225, 26]],
      flick: [1.225, 1.425],
      capFade: [1.35, 1.425],
      torn: 0.885,
      pourOn: [1.65, 1.78],
      glide: [2.2, 2.95],
      // Flavor panels: [start, end] of each rise. Panel 0 rises over the ink while the stick keeps pouring.
      marks: [[2.2, 2.95], [3.45, 4.2], [4.7, 5.45]],
      pourOff: [5.62, 5.82],
      total: 5.95,
    };

    // Stage poses: [cx (fraction of W), cy (fraction of H), rotation deg, scale]
    const LAYOUTS = {
      mobile: {
        story: [
          [-1.2, [0.66, 1.02, -30, 0.8]],
          [U.rest, [0.64, 0.565, -8, 1]],
          [0.65, [0.64, 0.565, -8, 1]],
          [0.875, [0.62, 0.555, 0, 1.03]],
          [1.425, [0.62, 0.555, 0, 1.03]],
          [1.725, [0.7, 0.42, -164, 0.96]],
          [2.15, [0.69, 0.435, -170, 0.96]],
        ],
        pour: [0.815, 0.185, -176, 0.6],
      },
      desktop: {
        story: [
          [-1.2, [0.7, 1.05, -26, 0.82]],
          [U.rest, [0.68, 0.54, -12, 1]],
          [0.65, [0.68, 0.54, -12, 1]],
          [0.875, [0.66, 0.53, 0, 1.03]],
          [1.425, [0.66, 0.53, 0, 1.03]],
          [1.725, [0.69, 0.41, -150, 0.92]],
          [2.15, [0.675, 0.43, -157, 0.92]],
        ],
        // Centered in the empty strip between the photo (0-40%) and the copy (60%+), clear of both.
        pour: [0.5, 0.29, -174, 0.6],
      },
    };

    class Pour {
      constructor(section, CX) {
        this.CX = CX;
        this.section = section;
        this.track = section.querySelector('[data-cx-track]');
        this.stage = section.querySelector('[data-cx-stage]');
        this.zone = section.querySelector('[data-cx-po-zone]');
        this.stick = section.querySelector('[data-cx-stick]');
        this.spin = section.querySelector('[data-cx-spin]');
        this.cap = section.querySelector('[data-cx-cap]');
        this.capImg = section.querySelector('[data-cx-cap-img]');
        this.frontImgs = Array.from(section.querySelectorAll('.cx-po__face--front [data-cx-face]'));
        this.backImgs = Array.from(section.querySelectorAll('.cx-po__face--back [data-cx-face]'));
        this.giant = section.querySelector('[data-cx-giant]');
        this.beats = Array.from(section.querySelectorAll('[data-cx-beat]'));
        this.steps = Array.from(section.querySelectorAll('[data-cx-step]'));
        this.stepList = section.querySelector('.cx-po__steps');
        this.panels = Array.from(section.querySelectorAll('[data-cx-panel]'));
        this.bgs = this.panels.map((panel) => panel.querySelector('[data-cx-fl-bg]'));
        this.bgIns = this.panels.map((panel) => panel.querySelector('[data-cx-fl-bg-in]'));
        this.photos = this.panels.map((panel) => panel.querySelector('[data-cx-fl-photo]'));
        this.imgs = this.panels.map((panel) => panel.querySelector('.cx-fl__img'));
        this.medias = this.panels.map((panel) => panel.querySelector('.cx-fl__media'));
        this.bgWraps = this.panels.map((panel) => panel.querySelector('.cx-fl__bg'));
        this.dots = Array.from(section.querySelectorAll('[data-cx-fl-go]'));
        this.nav = section.querySelector('.cx-fl__nav');
        this.keys = this.panels.map((panel) => panel.dataset.flavor);
        this.marks = U.marks.slice(0, this.panels.length);
        this.powder = new CX.Powder(section.querySelector('[data-cx-powder]'), CX);
        try {
          this.flavorMap = JSON.parse(section.querySelector('[data-cx-flavor-map]').textContent);
        } catch (error) {
          this.flavorMap = {};
        }
        this.active = -1;
        this.torn = null;
        this.faceKey = '';
        this.flavor = this.keys[0];
        this.setFaces();

        section.classList.add('is-pinned');
        this.panels.forEach((panel) => this.setInert(panel, true));

        this.onFlavor = (event) => {
          if (event.detail) this.useFlavor(event.detail.flavor);
        };
        document.addEventListener('cx:flavor', this.onFlavor);
        const hero = document.querySelector('[data-cx-hero]');
        if (hero && hero.dataset.flavor) this.useFlavor(hero.dataset.flavor);

        this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.cxFlGo))));
        // In-page links to the flavor picker land on the right flavor, not on the top of the pinned stage.
        this.onClick = (event) => {
          const link = event.target.closest && event.target.closest('a[href*="#cx-flavor"]');
          if (!link || link.pathname !== window.location.pathname) return;
          const index = this.indexForHash(link.hash);
          if (index < 0) return;
          event.preventDefault();
          this.go(index);
        };
        document.addEventListener('click', this.onClick);
        this.stopPredecode = CX.predecode(section, '120% 0px 120% 0px');
        this.removeScene = CX.addScene(this);
        if (window.location.hash) window.requestAnimationFrame(() => {
          const index = this.indexForHash(window.location.hash);
          if (index >= 0) this.go(index, true);
        });
      }

      destroy() {
        this.removeScene();
        if (this.stopPredecode) this.stopPredecode();
        document.removeEventListener('cx:flavor', this.onFlavor);
        document.removeEventListener('click', this.onClick);
        this.powder.set(0, null, [0, 1], 0);
        this.section.classList.remove('is-pinned');
        this.panels.forEach((panel) => this.setInert(panel, false));
        this.track.style.height = '';
        if (this.heroEl) this.heroEl.classList.remove('is-handoff');
      }

      indexForHash(hash) {
        if (hash === '#cx-flavors') return 0;
        return this.panels.findIndex((panel) => `#${panel.id}` === hash);
      }

      setInert(panel, inert) {
        const body = panel.querySelector('[data-cx-fl-body]');
        if (!body) return;
        body.inert = inert;
        if (inert) body.setAttribute('aria-hidden', 'true');
        else body.removeAttribute('aria-hidden');
      }

      // The face sequence the stick spins through: the torn story flavor, then each panel's flavor.
      setFaces() {
        this.faces = this.flavor === this.keys[0] ? this.keys.slice() : [this.flavor].concat(this.keys);
        this.flips = this.marks.map((m) => [m[0] + 0.12, m[1] - 0.08]);
        if (this.faces.length === this.keys.length) this.flips = this.flips.slice(1);
        this.faceKey = '';
      }

      // The story stick is whichever flavor the hero shows, so the stick you saw is the one you tear.
      useFlavor(key) {
        const entry = this.flavorMap[key];
        if (!entry || this.flavor === key) return;
        if (this.lastU !== undefined && this.lastU > 0.6) return;
        this.flavor = key;
        if (this.capImg.getAttribute('src') !== entry.src) this.capImg.setAttribute('src', entry.src);
        this.setFaces();
        if (this.lastU !== undefined) this.render(this.lastU, this.CX.view.y);
      }

      measure(view) {
        const { CX } = this;
        this.W = this.stage.offsetWidth;
        this.H = this.stage.offsetHeight;
        this.track.style.height = `${Math.round((U.total + 1) * this.H)}px`;
        this.top = CX.docTop(this.track);
        this.sw = this.stick.offsetWidth;
        this.sh = this.stick.offsetHeight;
        this.desktop = this.W >= 990;
        this.layout = this.desktop ? LAYOUTS.desktop : LAYOUTS.mobile;
        this.slant = this.desktop ? 0.05 * this.W : 0.12 * this.W;
        this.section.style.setProperty('--cx-slant', `${this.slant.toFixed(1)}px`);
        this.photoH = this.photos[0] ? this.photos[0].parentElement.offsetHeight : this.H;
        this.bgH = this.bgs[0] ? this.bgs[0].parentElement.offsetHeight : this.H;
        this.powder.resize(this.W, this.H);
        // The sticky buy bar hides while the flavor panels (with their own buttons) are on screen.
        if (this.zone) {
          this.zone.style.top = `${Math.round((this.marks[0][1] - 0.2) * this.H)}px`;
          this.zone.style.height = `${Math.round((U.total - this.marks[0][1] + 1.2) * this.H)}px`;
        }
        this.measureHero(view);
        this.lastU = undefined;
      }

      measureHero() {
        const { CX } = this;
        const hero = document.querySelector('[data-cx-hero]');
        this.heroEl = hero || null;
        this.heroPose = null;
        if (!hero) return;
        const heroBottom = CX.docTop(hero) + hero.offsetHeight;
        if (Math.abs(this.top - heroBottom) > 240) return;
        const stick = hero.querySelector('.cx-hero__product.is-active [data-cx-hero-stick]') || hero.querySelector('[data-cx-hero-stick]');
        if (!stick || !stick.offsetHeight) return;
        const box = stick.offsetParent;
        let left = 0;
        let node = box;
        while (node) {
          left += node.offsetLeft;
          node = node.offsetParent;
        }
        const top = CX.docTop(box);
        const rot = parseFloat(window.getComputedStyle(stick).getPropertyValue('--cx-rot')) || 0;
        this.heroPose = {
          x: left + stick.offsetLeft + stick.offsetWidth / 2,
          y: top + stick.offsetTop + stick.offsetHeight / 2,
          r: rot,
          s: stick.offsetHeight / this.sh,
        };
        this.handoffEnd = this.top + U.rest * this.H;
      }

      go(index, instant) {
        const i = Math.max(0, Math.min(this.marks.length - 1, index));
        const next = this.marks[i + 1];
        const rest = next ? (this.marks[i][1] + next[0]) / 2 : (this.marks[i][1] + U.pourOff[0]) / 2;
        const top = this.top + rest * this.H;
        window.scrollTo({ top, behavior: instant || this.CX.reduced ? 'auto' : 'smooth' });
      }

      update(view) {
        if (!this.H) return;
        const u = this.CX.clamp((view.y - this.top) / this.H, -1.5, U.total + 1);
        const inHandoff = this.heroPose && view.y <= this.handoffEnd + 2;
        if (u === this.lastU && !inHandoff) return;
        this.lastU = u;
        this.render(u, view.y);
      }

      pose(el, [cx, cy, r, s], opacity) {
        const x = cx * this.W - this.sw / 2;
        const y = cy * this.H - this.sh / 2;
        this.CX.set(el, `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) rotate(${r.toFixed(3)}deg) scale(${s.toFixed(5)})`, opacity);
      }

      storyPose(u, y) {
        const { CX, layout } = this;
        const rest = CX.kf(U.rest, layout.story);
        if (u >= U.rest || !this.heroPose) return CX.kf(u, layout.story);
        // Hand-off: from the hero's stick (moving with the page) into the stage's first pose.
        const hp = this.heroPose;
        const stageTop = Math.max(this.top - y, 0);
        const heroRel = [hp.x / this.W, (hp.y - y - stageTop) / this.H, hp.r, hp.s];
        const t = CX.clamp(y / Math.max(1, this.handoffEnd));
        const e = CX.ease.inOut(t);
        return heroRel.map((v, i) => CX.lerp(v, rest[i], e));
      }

      render(u, y) {
        const { CX, W, H } = this;
        const { seg, ease } = CX;

        // Giant "5g"
        CX.set(this.giant, `translate3d(-44%, calc(-40% + ${(-Math.min(u, 1) * 18).toFixed(2)}svh), 0)`, 1 - seg(u, U.giant[0], U.giant[1]));

        // Stick: the story path, then a glide into the pour pose it holds over the flavor panels.
        let pose = this.storyPose(u, y);
        if (u > U.glide[0]) {
          const g = ease.inOut(seg(u, U.glide[0], U.glide[1]));
          pose = pose.map((v, i) => CX.lerp(v, this.layout.pour[i], g));
        }
        // Flavor flips: one continuous Y-axis spin, swapping faces edge-on.
        let angle = 0;
        this.flips.forEach(([a, b]) => {
          angle += ease.inOut(seg(u, a, b));
        });
        angle *= 180;
        const local = angle / 180 - Math.floor(angle / 180);
        const lift = Math.sin(Math.PI * local);
        pose = [pose[0], pose[1] - lift * 0.02, pose[2] + lift * 6, pose[3]];
        // With the hero right above, the hero shows its own stick until the page scrolls.
        this.pose(this.stick, pose, this.heroPose && y <= 1 ? 0 : 1);
        CX.set(this.spin, `perspective(1400px) rotateY(${angle.toFixed(2)}deg)`);

        const front = this.faces[Math.min(this.faces.length - 1, 2 * Math.round(angle / 360))];
        const back = this.faces[Math.min(this.faces.length - 1, 2 * Math.floor(angle / 360) + 1)];
        const faceKey = `${front}|${back}`;
        if (faceKey !== this.faceKey) {
          this.faceKey = faceKey;
          this.frontImgs.forEach((img) => img.classList.toggle('is-on', img.dataset.cxFace === front));
          this.backImgs.forEach((img) => img.classList.toggle('is-on', img.dataset.cxFace === back));
        }
        const showing = this.faces[Math.min(this.faces.length - 1, Math.round(angle / 180))];
        if (showing !== this.powderKey) {
          this.powderKey = showing;
          const entry = this.flavorMap[showing];
          if (entry) this.powder.setColors(entry.powder);
        }

        const carrying = Boolean(this.heroPose) && y > 1;
        if (this.heroEl && carrying !== this.carrying) {
          this.carrying = carrying;
          this.heroEl.classList.toggle('is-handoff', carrying);
        }

        // Cap: peel (pivot on the right end of the tear), then flick away.
        const peel = CX.kf(u, U.peel);
        const flick = seg(u, U.flick[0], U.flick[1]);
        const fx = ease.in(flick) * this.sw * 1.6;
        const fy = -ease.out(flick) * this.sh * 0.42;
        CX.set(this.cap, `translate3d(${fx.toFixed(1)}px, ${fy.toFixed(1)}px, 0) rotate(${(peel + flick * 70).toFixed(2)}deg)`, 1 - seg(u, U.capFade[0], U.capFade[1]));
        const torn = u >= U.torn;
        if (torn !== this.torn) {
          this.torn = torn;
          this.stick.classList.toggle('is-torn', torn);
        }

        this.powder.edge = u > U.glide[0] ? 'rgba(24, 27, 40, 0.3)' : null;

        // Powder: one stream from the open end, from the pour beat to the end of the last flavor.
        const rate = seg(u, U.pourOn[0], U.pourOn[1]) * (1 - seg(u, U.pourOff[0], U.pourOff[1]));
        if (rate > 0) {
          const [cx, cy, rDeg, sc] = pose;
          const th = (rDeg * Math.PI) / 180;
          const L = (this.sh / 2 - TEAR_Y * this.sh) * sc;
          const flow = u > U.glide[0] ? 1400 : 1500;
          this.powder.set(rate * flow, [cx * W + L * Math.sin(th), cy * H - L * Math.cos(th)], [Math.sin(th), -Math.cos(th)], this.sw * sc * (u > U.glide[0] ? 0.42 : 0.34));
        } else {
          this.powder.set(0, null, [0, 1], 0);
        }

        // Flavor panels: each rises from the bottom with a slanted edge; the photo follows a beat later.
        const { slant } = this;
        let position = 0;
        this.marks.forEach(([a, b], i) => {
          const t = seg(u, a, b);
          position += t;
          if (i === 0) {
            // The first flavor rises over the ink as one sheet (photo and color together) under the stream.
            const e = ease.inOut(t);
            const y0 = (1 - e) * (this.H + slant);
            CX.set(this.medias[0], `translate3d(0, ${y0.toFixed(1)}px, 0)`);
            CX.set(this.bgWraps[0], `translate3d(0, ${y0.toFixed(1)}px, 0)`);
            CX.set(this.bgs[0], 'translate3d(0, 0, 0)');
            CX.set(this.bgIns[0], 'translate3d(0, 0, 0)');
            CX.set(this.photos[0], 'translate3d(0, 0, 0)');
            CX.set(this.imgs[0], `translate3d(0, ${(-(1 - e) * this.photoH * 0.18).toFixed(1)}px, 0) scale(${(1 + (1 - e) * 0.06).toFixed(4)})`);
            return;
          }
          const tb = ease.inOut(seg(t, 0, 0.8));
          const bgY = (1 - tb) * (this.bgH + slant);
          CX.set(this.bgs[i], `translate3d(0, ${bgY.toFixed(1)}px, 0)`);
          CX.set(this.bgIns[i], `translate3d(0, ${(-bgY).toFixed(1)}px, 0)`);
          const tp = ease.inOut(seg(t, 0.14, 1));
          const phY = (1 - tp) * (this.photoH + slant);
          CX.set(this.photos[i], `translate3d(0, ${phY.toFixed(1)}px, 0)`);
          CX.set(this.imgs[i], `translate3d(0, ${(-phY * 0.82).toFixed(1)}px, 0) scale(${(1 + (1 - tp) * 0.08).toFixed(4)})`);
          if (i > 0 && this.imgs[i - 1]) CX.set(this.imgs[i - 1], `translate3d(0, ${(-tp * this.photoH * 0.12).toFixed(1)}px, 0) scale(${(1 + tp * 0.04).toFixed(4)})`);
        });
        // The first panel's copy waits until its sheet has landed; later ones swap mid-wipe as before.
        const active = position < 0.88 ? -1 : Math.min(this.panels.length - 1, Math.max(0, Math.round(position) - 1));
        if (active !== this.active) {
          if (this.active >= 0) {
            this.panels[this.active].classList.remove('is-active');
            this.setInert(this.panels[this.active], true);
          }
          this.active = active;
          if (active >= 0) {
            this.panels[active].classList.add('is-active');
            this.setInert(this.panels[active], false);
            this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === active)));
          }
          this.section.dataset.active = String(active);
        }
        CX.set(this.nav, undefined, seg(u, this.marks[0][1] - 0.25, this.marks[0][1]));

        // Copy beats
        const windows = { intro: U.intro, tear: U.tear, pour: U.pourText };
        this.beats.forEach((el) => {
          const w = windows[el.dataset.cxBeat];
          if (!w) return;
          let o = 1;
          let dy = 0;
          if (u < w[0] || u > w[3]) {
            o = 0;
            dy = u < w[0] ? 28 : -28;
          } else if (u < w[1]) {
            const t = ease.out(seg(u, w[0], w[1]));
            o = t;
            dy = (1 - t) * 28;
          } else if (u > w[2]) {
            const t = ease.in(seg(u, w[2], w[3]));
            o = 1 - t;
            dy = -t * 28;
          }
          CX.set(el, `translate3d(0, ${dy.toFixed(1)}px, 0)`, o);
        });

        // Step labels (no progress rule): Tear, Pour, then Taste as the first flavor rises.
        const starts = [U.tear[0], U.pourText[0], this.marks[0][0]];
        let step = -1;
        starts.forEach((start, i) => {
          if (u >= start) step = i;
        });
        if (step !== this.step) {
          this.step = step;
          this.steps.forEach((el, i) => {
            el.classList.toggle('is-active', i === step);
            el.classList.toggle('is-done', i < step);
          });
        }
        CX.set(this.stepList, undefined, 1 - seg(u, this.marks[0][0] + 0.25, this.marks[0][0] + 0.55));
      }
    }

    const instances = new Map();
    const initAll = (CX, root = document) => {
      root.querySelectorAll('[data-cx-pour]').forEach((section) => {
        if (instances.has(section) || CX.reduced) return;
        instances.set(section, new Pour(section, CX));
      });
      window.CXPour = instances;
    };
    const destroyAll = (root = document) => {
      instances.forEach((instance, section) => {
        if (root === document || root.contains(section)) {
          instance.destroy();
          instances.delete(section);
        }
      });
    };

    (window.CXQ = window.CXQ || []).push((CX) => {
      if (!document.querySelector('[data-cx-pour]')) return;
      initAll(CX);
      CX.onReduceChange(() => {
        destroyAll();
        initAll(CX);
      });
      document.addEventListener('shopify:section:load', (event) => initAll(CX, event.target));
      document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
    });
  })();
})();

/* ---------- taste ---------- */
(() => {
  /* ---------- sections/cx-taste.liquid: struck complaints, then each flavor tears and pours ---------- */
  (() => {
    const TEAR_Y = 0.062;
    // Scroll budget in stage heights (u).
    const NOT_START = 0.05;
    const NOT_STEP = 0.2;
    const NOTS_OUT = [0.72, 0.9];
    const FLOOD0 = [0.78, 1.02];
    const TITLE = [0.9, 1.08];
    const MORE = [1.02, 1.2];
    const FAN = [0.95, 1.22];
    const TURN0 = 1.3;
    const TURN = 0.62;
    const TOTAL = 3.35;

    // [x, y, rotation, scale] as fractions of the stage.
    const LAYOUTS = {
      mobile: { fan: (k) => [0.3 + k * 0.2, 0.76, (k - 1) * 13, 0.9], pour: [0.8, 0.53, -166, 0.84] },
      desktop: { fan: (k) => [0.62 + k * 0.1, 0.6, (k - 1) * 12, 0.9], pour: [0.53, 0.42, -164, 0.95] },
    };

    class Taste {
      constructor(section, CX) {
        this.CX = CX;
        this.section = section;
        this.track = section.querySelector('[data-cx-ts-track]');
        this.stage = section.querySelector('[data-cx-ts-stage]');
        this.nots = Array.from(section.querySelectorAll('[data-cx-ts-not]')).map((li) => ({ li, strike: li.querySelector('.cx-ts__strike') }));
        this.title = section.querySelector('[data-cx-ts-title]');
        this.more = section.querySelector('[data-cx-ts-more]');
        this.floods = Array.from(section.querySelectorAll('[data-cx-ts-flood]'));
        this.sticks = Array.from(section.querySelectorAll('[data-cx-ts-stick]'));
        this.caps = this.sticks.map((s) => s.querySelector('[data-cx-ts-cap]'));
        this.powders = Array.from(section.querySelectorAll('[data-cx-ts-powder]')).map((canvas) => {
          const p = new CX.Powder(canvas, CX);
          p.setColors(canvas.dataset.colors || '#ffffff');
          // A faint ink edge keeps the light grains readable on the flavor backgrounds.
          p.edge = 'rgba(24, 27, 40, 0.32)';
          return p;
        });
        this.torn = this.sticks.map(() => false);
        section.classList.add('is-pinned');
        this.stopPredecode = CX.predecode(section);
        this.removeScene = CX.addScene(this);
      }

      destroy() {
        this.removeScene();
        if (this.stopPredecode) this.stopPredecode();
        this.powders.forEach((p) => p.set(0, null, [0, 1], 0));
        this.section.classList.remove('is-pinned');
        this.track.style.height = '';
        this.section.dataset.tone = 'ink';
        [...this.nots.flatMap((n) => [n.li, n.strike]), this.title, this.more, ...this.floods, ...this.sticks, ...this.caps].forEach((el) => {
          if (!el) return;
          el.style.transform = '';
          el.style.opacity = '';
        });
      }

      measure() {
        const { CX } = this;
        this.W = this.stage.offsetWidth;
        this.H = this.stage.offsetHeight;
        this.track.style.height = `${Math.round((TOTAL + 1) * this.H)}px`;
        this.top = CX.docTop(this.track);
        this.layout = this.W >= 990 ? LAYOUTS.desktop : LAYOUTS.mobile;
        this.sw = this.sticks[0] ? this.sticks[0].offsetWidth : 0;
        this.sh = this.sticks[0] ? this.sticks[0].offsetHeight : 0;
        this.powders.forEach((p) => p.resize(this.W, this.H));
        this.lastU = null;
      }

      update(view) {
        if (!this.H) return;
        const u = this.CX.clamp((view.y - this.top) / this.H, -1, TOTAL + 1);
        if (u === this.lastU) return;
        this.lastU = u;
        this.render(u, view);
      }

      pose(el, [x, y, r, s], o) {
        const tx = x * this.W - this.sw / 2;
        const ty = y * this.H - this.sh / 2;
        this.CX.set(el, `translate3d(${tx.toFixed(1)}px, ${ty.toFixed(1)}px, 0) rotate(${r.toFixed(2)}deg) scale(${s.toFixed(4)})`, o);
      }

      render(u, view) {
        const { CX } = this;
        const { seg, ease, lerp } = CX;
        const onScreen = view.y + view.h > this.top && view.y < this.top + (TOTAL + 1) * this.H;

        // 1. The usual creatine complaints slam in and get struck, one at a time.
        const out = ease.in(seg(u, NOTS_OUT[0], NOTS_OUT[1]));
        this.nots.forEach(({ li, strike }, i) => {
          const a = NOT_START + i * NOT_STEP;
          const t = ease.out(seg(u, a, a + 0.08));
          const s = ease.inOut(seg(u, a + 0.08, a + 0.17));
          CX.set(li, `translate3d(0, ${(-out * 2.2).toFixed(2)}rem, 0) scale(${(1.22 - 0.22 * t).toFixed(4)})`, t * (1 - out));
          CX.set(strike, `rotate(-4deg) scaleX(${s.toFixed(4)})`);
        });

        // 2. The first flavor floods in, the headline lands, the sticks fan up from below.
        const floodStarts = [FLOOD0[0]].concat(this.sticks.slice(1).map((_, i) => TURN0 + (i + 1) * TURN - 0.08));
        let tone = 'ink';
        this.floods.forEach((flood, i) => {
          const a = floodStarts[i] ?? 99;
          const t = ease.inOut(seg(u, a, a + 0.24));
          CX.set(flood, `translate3d(0, ${((1 - t) * 100).toFixed(2)}%, 0)`, t > 0 ? 1 : 0);
          if (t > 0.5) tone = flood.dataset.tone || 'dark';
        });
        if (tone !== this.tone) {
          this.tone = tone;
          this.section.dataset.tone = tone;
        }
        const ti = ease.out(seg(u, TITLE[0], TITLE[1]));
        CX.set(this.title, `translate3d(0, ${((1 - ti) * 1.6).toFixed(2)}rem, 0) scale(${(1.08 - 0.08 * ti).toFixed(4)})`, ti);
        const mo = ease.out(seg(u, MORE[0], MORE[1]));
        CX.set(this.more, `translate3d(0, ${((1 - mo) * 1).toFixed(2)}rem, 0)`, mo);

        // 3. Each flavor in turn: jumps to the pour pose (tearing on the way), pours, flies off.
        const L = this.layout;
        this.sticks.forEach((el, k) => {
          const f = ease.out(seg(u, FAN[0] + k * 0.05, FAN[1] + k * 0.05));
          const slot = L.fan(k);
          let pose = [slot[0], lerp(1.35, slot[1], f), lerp(slot[2] + 30, slot[2], f), slot[3]];
          let o = f > 0 ? 1 : 0;
          const a = TURN0 + k * TURN;
          const go = ease.inOut(seg(u, a, a + 0.16));
          if (go > 0) pose = pose.map((v, i) => lerp(v, L.pour[i], go));
          const last = k === this.sticks.length - 1;
          const exit = last ? 0 : ease.in(seg(u, a + 0.5, a + 0.64));
          if (exit > 0) pose = [lerp(L.pour[0], L.pour[0] + 0.5, exit), lerp(L.pour[1], L.pour[1] - 0.55, exit), L.pour[2] + exit * 40, L.pour[3]];
          if (exit >= 1) o = 0;
          this.pose(el, pose, o);

          // Tear while it travels: the cap peels, then flicks off.
          const peel = CX.kf(u, [[a - 0.06, 0], [a + 0.02, 12], [a + 0.08, 26]]);
          const flick = seg(u, a + 0.08, a + 0.18);
          CX.set(this.caps[k], `translate3d(${(ease.in(flick) * this.sw * 1.6).toFixed(1)}px, ${(-ease.out(flick) * this.sh * 0.42).toFixed(1)}px, 0) rotate(${(peel + flick * 70).toFixed(2)}deg)`, 1 - seg(u, a + 0.14, a + 0.18));
          const torn = u >= a - 0.05;
          if (torn !== this.torn[k]) {
            this.torn[k] = torn;
            el.classList.toggle('is-torn', torn);
          }

          const rate = onScreen ? seg(u, a + 0.14, a + 0.2) * (1 - seg(u, a + 0.46, a + 0.52)) : 0;
          const powder = this.powders[k];
          if (!powder) return;
          if (rate > 0) {
            const th = (pose[2] * Math.PI) / 180;
            const len = (this.sh / 2 - TEAR_Y * this.sh) * pose[3];
            powder.set(rate * 1300, [pose[0] * this.W + len * Math.sin(th), pose[1] * this.H - len * Math.cos(th)], [Math.sin(th), -Math.cos(th)], this.sw * pose[3] * 0.34);
          } else {
            powder.set(0, null, [0, 1], 0);
          }
        });
      }
    }

    const instances = new Map();
    const initAll = (CX, root = document) => {
      root.querySelectorAll('[data-cx-taste]').forEach((section) => {
        if (instances.has(section) || CX.reduced) return;
        instances.set(section, new Taste(section, CX));
      });
    };
    const destroyAll = (root = document) => {
      instances.forEach((instance, section) => {
        if (root === document || root.contains(section)) {
          instance.destroy();
          instances.delete(section);
        }
      });
    };

    (window.CXQ = window.CXQ || []).push((CX) => {
      if (!document.querySelector('[data-cx-taste]')) return;
      initAll(CX);
      CX.onReduceChange(() => {
        destroyAll();
        initAll(CX);
      });
      document.addEventListener('shopify:section:load', (event) => initAll(CX, event.target));
      document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
    });
  })();
})();

/* ---------- versus ---------- */
(() => {
  /* ---------- sections/cx-versus.liquid: powder gets its X's, then CreaStix tears and pours ---------- */
  (() => {
    const TEAR_Y = 0.062;
    // Scroll budget in stage heights (u). The ritual keeps the home page's tear/pour pacing.
    const X_START = 0.1;
    const X_STEP = 0.09;
    const WIPE = [1.0, 1.35];
    const R = {
      enter: [1.3, 1.55],
      tear: [1.6, 1.72, 2.35, 2.47],
      straighten: [1.7, 1.82],
      peel: [[1.82, 0], [1.95, 9], [2.08, 26]],
      flick: [2.08, 2.22],
      capFade: [2.18, 2.22],
      torn: 1.83,
      check1: 2.0,
      pour: [2.5, 2.62, 3.3, 3.42],
      flip: [2.45, 2.72],
      pourOn: [2.68, 2.76],
      pourOff: [3.3, 3.4],
      exit: [3.42, 3.58],
      check2: 2.8,
      close: [3.52, 3.66],
      total: 3.85,
    };
    // Desktop shows both cards at once, so the ritual starts sooner and there is no wipe.
    const DESKTOP_SHIFT = -0.6;

    // [cx, cy, rotation, scale] as fractions of the CreaStix card.
    const POSES = {
      mobile: { from: [0.74, 1.3, -26, 0.9], rest: [0.72, 0.62, -8, 1], straight: [0.7, 0.61, 0, 1.03], pour: [0.74, 0.47, -166, 0.96] },
      desktop: { from: [0.72, 1.3, -24, 0.9], rest: [0.7, 0.57, -12, 1], straight: [0.68, 0.56, 0, 1.03], pour: [0.68, 0.5, -157, 0.92] },
    };

    class Versus {
      constructor(section, CX) {
        this.CX = CX;
        this.section = section;
        this.track = section.querySelector('[data-cx-vs-track]');
        this.stage = section.querySelector('[data-cx-vs-stage]');
        this.fresh = section.querySelector('[data-cx-vs-new]');
        this.oldSteps = Array.from(section.querySelectorAll('[data-cx-vs-step]'));
        this.foot = section.querySelector('[data-cx-vs-foot]');
        this.badge = section.querySelector('[data-cx-vs-badge]');
        this.badgeIn = this.badge.firstElementChild;
        this.beats = Array.from(section.querySelectorAll('[data-cx-vs-two]'));
        this.stick = section.querySelector('[data-cx-vs-stick]');
        this.cap = section.querySelector('[data-cx-vs-cap]');
        this.close = section.querySelector('[data-cx-vs-close]');
        this.cta = section.querySelector('[data-cx-vs-cta]');
        const canvas = section.querySelector('[data-cx-vs-powder]');
        this.powder = new CX.Powder(canvas, CX);
        this.powder.setColors(canvas.dataset.colors || '#ffffff');
        section.classList.add('is-pinned');
        if (this.cta) this.cta.tabIndex = -1;
        this.stopPredecode = CX.predecode(section);
        this.removeScene = CX.addScene(this);
      }

      destroy() {
        this.removeScene();
        if (this.stopPredecode) this.stopPredecode();
        this.powder.set(0, null, [0, 1], 0);
        this.section.classList.remove('is-pinned');
        this.track.style.height = '';
        if (this.cta) this.cta.tabIndex = 0;
        [this.fresh, this.badge, this.badgeIn, this.stick, this.cap, this.close, ...this.beats].forEach((el) => {
          el.style.transform = '';
          el.style.opacity = '';
        });
        this.stick.classList.remove('is-torn');
      }

      measure() {
        const { CX } = this;
        this.H = this.stage.offsetHeight;
        this.desktop = this.stage.offsetWidth >= 990;
        this.shift = this.desktop ? DESKTOP_SHIFT : 0;
        this.total = R.total + this.shift;
        this.track.style.height = `${Math.round((this.total + 1) * this.H)}px`;
        this.top = CX.docTop(this.track);
        this.cw = this.fresh.offsetWidth;
        this.ch = this.fresh.offsetHeight;
        this.sw = this.stick.offsetWidth;
        this.sh = this.stick.offsetHeight;
        this.poses = this.desktop ? POSES.desktop : POSES.mobile;
        this.powder.resize(this.cw, this.ch);
        this.lastU = null;
      }

      update(view) {
        if (!this.H) return;
        const u = this.CX.clamp((view.y - this.top) / this.H, -1, this.total + 1);
        if (u === this.lastU) return;
        this.lastU = u;
        this.render(u, view.y + view.h > this.top && view.y < this.top + (this.total + 1) * this.H);
      }

      pose([x, y, r, s], o) {
        const tx = x * this.cw - this.sw / 2;
        const ty = y * this.ch - this.sh / 2;
        this.CX.set(this.stick, `translate3d(${tx.toFixed(1)}px, ${ty.toFixed(1)}px, 0) rotate(${r.toFixed(2)}deg) scale(${s.toFixed(4)})`, o);
      }

      beat(el, w, u) {
        const { seg, ease } = this.CX;
        let o = 1;
        let dy = 0;
        if (u < w[0] || u > w[3]) {
          o = 0;
          dy = u < w[0] ? 28 : -28;
        } else if (u < w[1]) {
          const t = ease.out(seg(u, w[0], w[1]));
          o = t;
          dy = (1 - t) * 28;
        } else if (u > w[2]) {
          const t = ease.in(seg(u, w[2], w[3]));
          o = 1 - t;
          dy = -t * 28;
        }
        const base = this.desktop ? ' translateY(-50%)' : '';
        this.CX.set(el, `translate3d(0, ${dy.toFixed(1)}px, 0)${base}`, o);
      }

      render(u, onScreen) {
        const { CX } = this;
        const { seg, ease, lerp, kf } = CX;
        const s = this.shift;
        const at = (v) => v + s;

        // 1. Creatine powder: an X on every step, in reading order.
        this.oldSteps.forEach((li, k) => li.classList.toggle('is-on', u >= X_START + k * X_STEP));
        if (this.foot) this.foot.classList.toggle('is-on', u >= X_START + this.oldSteps.length * X_STEP);

        // 2. VS, and (mobile) the CreaStix card wipes up over the powder card.
        if (this.desktop) {
          CX.set(this.badgeIn, `scale(${this.pop(seg(u, 0.02, 0.14)).toFixed(4)})`);
        } else {
          const w = ease.inOut(seg(u, WIPE[0], WIPE[1]));
          CX.set(this.fresh, `translate3d(0, ${((1 - w) * 101).toFixed(2)}%, 0)`);
          CX.set(this.badge, `translate3d(0, ${((1 - w) * this.H).toFixed(1)}px, 0)`);
          const pop = this.pop(seg(u, WIPE[0] - 0.06, WIPE[0] + 0.06));
          const gone = ease.in(seg(u, WIPE[1] - 0.02, WIPE[1] + 0.1));
          CX.set(this.badgeIn, `scale(${(pop * (1 - gone)).toFixed(4)})`);
        }

        // 3. The ritual: rest, straighten, tear, flip, pour.
        const P = this.poses;
        let pose = kf(u, [[at(R.enter[0]), P.from], [at(R.enter[1]), P.rest]], ease.out);
        if (u > at(R.straighten[0])) pose = kf(u, [[at(R.straighten[0]), P.rest], [at(R.straighten[1]), P.straight]]);
        if (u > at(R.flip[0])) pose = kf(u, [[at(R.flip[0]), P.straight], [at(R.flip[1]), P.pour]]);
        if (u > at(R.flip[1])) pose = [P.pour[0], P.pour[1] + 0.012 * seg(u, at(R.flip[1]), at(R.pourOff[1])), P.pour[2] - 4 * seg(u, at(R.flip[1]), at(R.pourOff[1])), P.pour[3]];
        // After the pour the stick flies off so the closing line has the stage to itself.
        const exit = ease.in(seg(u, at(R.exit[0]), at(R.exit[1])));
        if (exit > 0) pose = [lerp(pose[0], pose[0] + 0.45, exit), lerp(pose[1], pose[1] - 0.7, exit), pose[2] + exit * 35, pose[3]];
        this.pose(pose, u >= at(R.enter[0]) && exit < 1 ? 1 : 0);

        const peel = kf(u, R.peel.map(([t, v]) => [at(t), v]));
        const flick = seg(u, at(R.flick[0]), at(R.flick[1]));
        CX.set(this.cap, `translate3d(${(ease.in(flick) * this.sw * 1.6).toFixed(1)}px, ${(-ease.out(flick) * this.sh * 0.42).toFixed(1)}px, 0) rotate(${(peel + flick * 70).toFixed(2)}deg)`, 1 - seg(u, at(R.capFade[0]), at(R.capFade[1])));
        const torn = u >= at(R.torn);
        if (torn !== this.torn) {
          this.torn = torn;
          this.stick.classList.toggle('is-torn', torn);
        }

        const rate = onScreen ? seg(u, at(R.pourOn[0]), at(R.pourOn[1])) * (1 - seg(u, at(R.pourOff[0]), at(R.pourOff[1]))) : 0;
        if (rate > 0) {
          const th = (pose[2] * Math.PI) / 180;
          const L = (this.sh / 2 - TEAR_Y * this.sh) * pose[3];
          this.powder.set(rate * 1500, [pose[0] * this.cw + L * Math.sin(th), pose[1] * this.ch - L * Math.cos(th)], [Math.sin(th), -Math.cos(th)], this.sw * pose[3] * 0.34);
        } else {
          this.powder.set(0, null, [0, 1], 0);
        }

        // Beats: "01 Tear." then "02 Pour.", each checked off as it happens.
        const windows = [R.tear, R.pour];
        const checks = [R.check1, R.check2];
        this.beats.forEach((el, i) => {
          const w = windows[i];
          if (!w) return;
          this.beat(el, w.map(at), u);
          el.classList.toggle('is-on', u >= at(checks[i]));
        });

        const c = ease.out(seg(u, at(R.close[0]), at(R.close[1])));
        CX.set(this.close, `translate3d(0, ${((1 - c) * 1.2).toFixed(2)}rem, 0)`, c);
        const live = c > 0.6;
        if (live !== this.live) {
          this.live = live;
          this.close.classList.toggle('is-live', live);
          if (this.cta) this.cta.tabIndex = live ? 0 : -1;
        }
      }

      // Overshoot pop for the VS badge: 0 -> 1.18 -> 1.
      pop(t) {
        if (t <= 0) return 0;
        if (t >= 1) return 1;
        return t < 0.6 ? this.CX.ease.out(t / 0.6) * 1.18 : 1.18 - 0.18 * this.CX.ease.inOut((t - 0.6) / 0.4);
      }
    }

    const instances = new Map();
    const initAll = (CX, root = document) => {
      root.querySelectorAll('[data-cx-vs]').forEach((section) => {
        if (instances.has(section) || CX.reduced) return;
        instances.set(section, new Versus(section, CX));
      });
    };
    const destroyAll = (root = document) => {
      instances.forEach((instance, section) => {
        if (root === document || root.contains(section)) {
          instance.destroy();
          instances.delete(section);
        }
      });
    };

    (window.CXQ = window.CXQ || []).push((CX) => {
      if (!document.querySelector('[data-cx-vs]')) return;
      initAll(CX);
      CX.onReduceChange(() => {
        destroyAll();
        initAll(CX);
      });
      document.addEventListener('shopify:section:load', (event) => initAll(CX, event.target));
      document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
    });
  })();
})();

/* ---------- places ---------- */
(() => {
  (() => {
    // Deal points (share of the pinned distance). The first print lands as the stage settles.
    const FIRST = -0.06;
    const SECOND = 0.1;
    const LAST = 0.8;

    class Places {
      constructor(section, CX) {
        this.CX = CX;
        this.section = section;
        this.track = section.querySelector('[data-cx-pl-track]');
        this.stage = section.querySelector('[data-cx-pl-stage]');
        this.cards = Array.from(section.querySelectorAll('[data-cx-pl-card]')).map((li, i) => ({
          li,
          i,
          par: li.querySelector('[data-cx-pl-par]'),
          deal: li.querySelector('[data-cx-pl-deal]'),
          air: li.querySelector('[data-cx-pl-air]'),
          rest: li.querySelector('[data-cx-pl-rest]'),
          depth: [0.9, -0.6, 0.5, -0.8, 0.7, -0.4][i % 6],
          dealt: false,
          anims: null,
        }));
        this.digits = Array.from(section.querySelectorAll('.cx-pl__count [data-cx-dg]'));
        this.capBox = section.querySelector('[data-cx-pl-cap]');
        this.capTitle = section.querySelector('[data-cx-pl-cap-title]');
        this.capDetail = section.querySelector('[data-cx-pl-cap-detail]');
        this.top = 0;
        this.topCard = -1;
        section.classList.add('is-pinned');
        this.cards.forEach((card) => this.build(card));
        // The prints start off-stage, so lazy loading would wait too long: fetch and decode them early.
        this.stopPredecode = CX.predecode(section);
        this.removeScene = CX.addScene(this);
      }

      destroy() {
        this.removeScene();
        if (this.stopPredecode) this.stopPredecode();
        this.section.classList.remove('is-pinned');
        this.cards.forEach((card) => card.anims && card.anims.forEach((anim) => anim.cancel()));
      }

      // One WAAPI animation set per card, scrubbed forwards (deal) or backwards (pick up) on demand.
      build(card) {
        const side = card.i % 2 === 0 ? -1 : 1;
        const css = card.li.style;
        const r = parseFloat(css.getPropertyValue('--cx-r')) || 0;
        const x = parseFloat(css.getPropertyValue('--cx-x')) || 0;
        const y = parseFloat(css.getPropertyValue('--cx-y')) || 0;
        const at = (dx, dy, rot, sc) => `translate3d(${(x + dx).toFixed(3)}rem, ${(y + dy).toFixed(3)}rem, 0) rotate(${(r + rot).toFixed(2)}deg) scale(${sc})`;
        // Thrown in from fully off-stage (viewport offset plus the card's own size), bottom corner alternating.
        const from = `translate3d(calc(${side} * (48vw + 100%)), calc(48vh + 90%), 0) rotate(${side * 32 - 8}deg) scale(1.12)`;
        const timing = { duration: 760, easing: 'linear', fill: 'both' };
        // Physical deal: fast throw, overshoot past the pile, a small counter-swing, settle.
        const deal = card.deal.animate(
          [
            { transform: from, easing: 'cubic-bezier(0.16, 0.7, 0.3, 1)' },
            { transform: at(-side * 0.55, -0.5, -side * 3.5, 1.015), offset: 0.62, easing: 'cubic-bezier(0.4, 0, 0.3, 1)' },
            { transform: at(side * 0.12, 0.08, side * 0.8, 0.997), offset: 0.84, easing: 'ease-in-out' },
            { transform: at(0, 0, 0, 1) },
          ],
          timing
        );
        // Soft shadow: wide and lifted in the air, tight on landing.
        const air = card.air.animate(
          [
            { opacity: 0.55, transform: 'translate3d(0, 2.2rem, 0) scale(1.08)' },
            { opacity: 0.35, transform: 'translate3d(0, 1.2rem, 0) scale(1.04)', offset: 0.6 },
            { opacity: 0, transform: 'translate3d(0, 0.4rem, 0) scale(1)' },
          ],
          timing
        );
        const rest = card.rest.animate([{ opacity: 0 }, { opacity: 0, offset: 0.55 }, { opacity: 1 }], timing);
        card.anims = [deal, air, rest];
        card.anims.forEach((anim) => anim.pause());
      }

      setDealt(card, dealt, instant) {
        if (card.dealt === dealt) return;
        card.dealt = dealt;
        card.anims.forEach((anim) => {
          if (instant) {
            anim.pause();
            anim.currentTime = dealt ? 760 : 0;
            return;
          }
          anim.playbackRate = dealt ? 1 : -1.35;
          anim.play();
        });
      }

      measure() {
        this.top = this.CX.docTop(this.track);
        this.H = this.stage.offsetHeight;
        this.distance = Math.max(1, this.track.offsetHeight - this.H);
        this.lastP = null;
      }

      update(view) {
        const { CX } = this;
        const p = CX.clamp((view.y - this.top) / this.distance, -0.2, 1.2);
        if (p === this.lastP) return;
        const first = this.lastP === null;
        this.lastP = p;
        const n = this.cards.length;
        const step = n > 2 ? (LAST - SECOND) / (n - 2) : 0;
        let top = -1;
        this.cards.forEach((card, i) => {
          const at = i === 0 ? FIRST : SECOND + (i - 1) * step;
          // Hysteresis so a card never flickers when you stop right on its threshold.
          const dealt = card.dealt ? p > at - 0.02 : p >= at;
          this.setDealt(card, dealt, first);
          if (dealt) top = i;
          // Light parallax per card while the stage is pinned.
          const drift = CX.clamp(p - at, -0.3, 0.6) * card.depth * 2.2;
          CX.set(card.par, `translate3d(0, ${(-drift).toFixed(2)}rem, 0)`);
        });
        this.setCaption(Math.max(0, top), first);
      }

      setCaption(index, instant) {
        if (index === this.topCard) return;
        const card = this.cards[index];
        if (!card) return;
        const dir = index > this.topCard ? -1 : 1;
        this.topCard = index;
        const label = String(index + 1).padStart(2, '0');
        this.digits.forEach((digit, i) => this.CX.rollDigit(digit, label[i], !instant, dir));
        this.capTitle.textContent = card.li.dataset.caption || '';
        this.capDetail.textContent = card.li.dataset.detail || '';
        if (!instant && !this.CX.reduced && this.capBox.animate) {
          this.capBox.animate(
            [{ opacity: 0, transform: `translate3d(0, ${dir * -0.5}rem, 0)` }, { opacity: 1, transform: 'none' }],
            { duration: 360, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }
          );
        }
      }
    }

    const instances = new Map();
    const initAll = (CX, root = document) => {
      root.querySelectorAll('[data-cx-places]').forEach((section) => {
        if (instances.has(section) || CX.reduced || !Element.prototype.animate) return;
        instances.set(section, new Places(section, CX));
      });
      window.CXPlaces = instances;
    };
    const destroyAll = (root = document) => {
      instances.forEach((instance, section) => {
        if (root === document || root.contains(section)) {
          instance.destroy();
          instances.delete(section);
        }
      });
    };

    (window.CXQ = window.CXQ || []).push((CX) => {
      if (!document.querySelector('[data-cx-places]')) return;
      initAll(CX);
      CX.onReduceChange(() => {
        destroyAll();
        initAll(CX);
      });
      document.addEventListener('shopify:section:load', (event) => initAll(CX, event.target));
      document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
    });
  })();
})();

/* ---------- stack ---------- */
(() => {
  (() => {
    // Desktop only: a small pointer tilt on the product media (transform only, one rAF per move).
    // Mouse pointers on wide screens only, so touch and hybrid devices never tilt.
    const canTilt = (event) => event.pointerType === 'mouse' && window.innerWidth >= 990 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const init = (root) => {
      root.querySelectorAll('[data-cx-stack] [data-cx-tilt], [data-cx-grid] [data-cx-tilt]').forEach((media) => {
        if (media.dataset.cxTiltReady) return;
        media.dataset.cxTiltReady = '1';
        const inner = media.querySelector('[data-cx-tilt-in]');
        if (!inner) return;
        let rect = null;
        let frame = 0;
        let px = 0.5;
        let py = 0.5;
        const paint = () => {
          frame = 0;
          const rx = (0.5 - py) * 7;
          const ry = (px - 0.5) * 9;
          inner.style.transform = `perspective(900px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg) translate3d(0, -4px, 0) scale(1.02)`;
        };
        media.addEventListener('pointerenter', (event) => {
          if (!canTilt(event)) return;
          rect = media.getBoundingClientRect();
          inner.classList.add('is-tilting');
        });
        media.addEventListener('pointermove', (event) => {
          if (!canTilt(event)) return;
          if (!rect) rect = media.getBoundingClientRect();
          px = (event.clientX - rect.left) / rect.width;
          py = (event.clientY - rect.top) / rect.height;
          if (!frame) frame = window.requestAnimationFrame(paint);
        }, { passive: true });
        media.addEventListener('pointerleave', () => {
          rect = null;
          if (frame) window.cancelAnimationFrame(frame);
          frame = 0;
          inner.classList.remove('is-tilting');
          inner.style.transform = '';
        });
      });
    };

    (window.CXQ = window.CXQ || []).push(() => {
      init(document);
      document.addEventListener('shopify:section:load', (event) => init(event.target));
    });
  })();
})();

/* ---------- sticky ---------- */
(() => {
  (() => {
    const initBar = (bar) => {
      if (bar.dataset.cxReady === 'true') return;
      bar.dataset.cxReady = 'true';

      const picks = Array.from(bar.querySelectorAll('[data-cx-sticky-pick]'));
      const apply = (key) => {
        if (!picks.some((pick) => pick.dataset.cxStickyPick === key)) return;
        bar.dataset.flavor = key;
        picks.forEach((pick) => pick.setAttribute('aria-pressed', String(pick.dataset.cxStickyPick === key)));
        bar.querySelectorAll('[data-cx-sticky-flavor]').forEach((deal) => {
          deal.hidden = deal.dataset.cxStickyFlavor !== key;
        });
      };

      picks.forEach((pick) => {
        pick.addEventListener('click', () => {
          const key = pick.dataset.cxStickyPick;
          if (document.querySelector('[data-cx-hero], [data-cx-product]')) document.dispatchEvent(new CustomEvent('cx:flavor-request', { detail: { flavor: key } }));
          apply(key);
        });
      });
      document.addEventListener('cx:flavor', (event) => {
        if (event.detail) apply(event.detail.flavor);
      });

      // Visible once the hero (or the offer banner if there is no hero) has scrolled away; hidden over
      // sections that carry their own buy buttons ([data-cx-bar-hide]) and over the footer.
      const anchor = document.querySelector('[data-cx-sticky-anchor]') || document.querySelector('[data-cx-hero]') || document.querySelector('[data-cx-offer]');
      const zones = Array.from(document.querySelectorAll('[data-cx-bar-hide], footer, .shopify-section-group-footer-group'));
      const covering = new Set();
      let anchorVisible = Boolean(anchor);
      const sync = () => {
        const hide = anchorVisible || covering.size > 0;
        if (bar.hidden !== hide) bar.hidden = hide;
      };

      if (!('IntersectionObserver' in window)) {
        bar.hidden = false;
        return;
      }
      if (anchor) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            anchorVisible = entry.isIntersecting;
          });
          sync();
        }, { rootMargin: '-140px 0px 0px 0px' }).observe(anchor);
      }
      const zoneIO = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) covering.add(entry.target);
          else covering.delete(entry.target);
        });
        sync();
      }, { rootMargin: '0px 0px -30% 0px' });
      zones.forEach((zone) => zoneIO.observe(zone));
      sync();
    };

    const init = (root) => root.querySelectorAll('[data-cx-sticky]').forEach(initBar);
    (window.CXQ = window.CXQ || []).push(() => {
      init(document);
      document.addEventListener('shopify:section:load', (event) => init(event.target));
    });
  })();
})();

/* ---------- product ---------- */
(() => {
  /* ---------- sections/cx-product.liquid: flavor, purchase option, gallery, quantity ---------- */
  (() => {
    const initProduct = (section) => {
      if (section.dataset.cxReady === 'true') return;
      section.dataset.cxReady = 'true';

      let variants = {};
      try {
        variants = JSON.parse(section.querySelector('[data-cx-pd-variants]').textContent);
      } catch (error) {
        variants = {};
      }
      const form = section.querySelector('[data-cx-pd-form]');
      const idInput = section.querySelector('[data-cx-pd-id]');
      const planInput = section.querySelector('[data-cx-pd-plan]');
      const picks = Array.from(section.querySelectorAll('[data-cx-pd-pick]'));
      const kinds = Array.from(section.querySelectorAll('[data-cx-pd-kind]'));
      const thumbs = Array.from(section.querySelectorAll('[data-cx-pd-thumb]'));
      const medias = Array.from(section.querySelectorAll('[data-cx-pd-media]'));
      const submit = section.querySelector('[data-cx-pd-submit]');
      const label = section.querySelector('[data-cx-pd-label]');
      const qty = section.querySelector('[data-cx-pd-qty-input]');

      const showMedia = (id) => {
        const key = String(id);
        if (!medias.some((m) => m.dataset.cxPdMedia === key)) return;
        medias.forEach((m) => m.classList.toggle('is-active', m.dataset.cxPdMedia === key));
        thumbs.forEach((t) => t.setAttribute('aria-pressed', String(t.dataset.cxPdThumb === key)));
      };

      const sync = () => {
        const flavor = section.dataset.flavor;
        const kind = section.dataset.kind;
        const v = variants[flavor];
        section.querySelectorAll('[data-cx-pd-flavor]').forEach((el) => {
          const on = el.dataset.cxPdFlavor === flavor;
          if (el.matches('[data-cx-pd-price]')) el.hidden = !(on && el.dataset.kind === kind);
          else if (el.classList.contains('cx-pd__bg') || el.classList.contains('cx-pd__name')) el.classList.toggle('is-active', on);
          else el.hidden = !on;
        });
        if (!v) return;
        idInput.value = v.id;
        planInput.value = kind === 'sub' && v.plan ? v.plan : '';
        if (submit) {
          submit.disabled = !v.available;
          if (label) label.textContent = v.available ? submit.dataset.label : submit.dataset.sold;
        }
      };

      const selectFlavor = (flavor, source) => {
        if (!variants[flavor]) return;
        section.dataset.flavor = flavor;
        picks.forEach((pick) => {
          pick.checked = pick.value === flavor;
        });
        thumbs.forEach((t) => {
          t.hidden = !(t.dataset.mediaFlavor === 'all' || t.dataset.mediaFlavor === flavor);
        });
        if (variants[flavor].media) showMedia(variants[flavor].media);
        sync();
        if (source === 'user') {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variants[flavor].id);
          window.history.replaceState(window.history.state, '', url.toString());
        }
        document.dispatchEvent(new CustomEvent('cx:flavor', { detail: { flavor, source } }));
      };

      picks.forEach((pick) => pick.addEventListener('change', () => selectFlavor(pick.value, 'user')));
      kinds.forEach((radio) => radio.addEventListener('change', () => {
        if (!radio.checked) return;
        section.dataset.kind = radio.value;
        sync();
      }));
      thumbs.forEach((t) => t.addEventListener('click', () => showMedia(t.dataset.cxPdThumb)));
      section.querySelectorAll('[data-cx-pd-qty]').forEach((btn) => btn.addEventListener('click', () => {
        const next = Math.max(1, Math.min(99, (parseInt(qty.value, 10) || 1) + Number(btn.dataset.cxPdQty)));
        qty.value = String(next);
      }));
      document.addEventListener('cx:flavor-request', (event) => {
        if (event.detail && section.isConnected) selectFlavor(event.detail.flavor, 'user');
      });

      // ?purchase=once opens on the one-time option (links from elsewhere that promise the offer price).
      const params = new URLSearchParams(window.location.search);
      if (params.get('purchase') === 'once' || (params.has('selling_plan') && !params.get('selling_plan'))) {
        const once = kinds.find((radio) => radio.value === 'once');
        if (once) {
          once.checked = true;
          section.dataset.kind = 'once';
        }
      }
      const checked = kinds.find((radio) => radio.checked);
      if (checked) section.dataset.kind = checked.value;
      selectFlavor(section.dataset.flavor, 'init');
      if (form) form.addEventListener('submit', sync);
    };

    const init = (root) => root.querySelectorAll('[data-cx-product]').forEach(initProduct);
    (window.CXQ = window.CXQ || []).push(() => {
      init(document);
      document.addEventListener('shopify:section:load', (event) => init(event.target));
    });
  })();
})();

/* ---------- reviews ---------- */
(() => {
  /* ---------- sections/cx-reviews.liquid: the review rows drift; pause on demand and off-screen ---------- */
  (() => {
    const initReviews = (section, CX) => {
      if (section.dataset.cxReady === 'true') return;
      section.dataset.cxReady = 'true';
      const pause = section.querySelector('[data-cx-rv-pause]');
      if (pause && !CX.reduced) {
        pause.hidden = false;
        pause.addEventListener('click', () => {
          const paused = pause.getAttribute('aria-pressed') !== 'true';
          pause.setAttribute('aria-pressed', String(paused));
          section.classList.toggle('is-paused', paused);
        });
      }
      // No drifting (and no repaints) while the banner is off-screen.
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => section.classList.toggle('is-away', !entry.isIntersecting));
        }, { rootMargin: '10% 0px' }).observe(section);
      }
    };

    (window.CXQ = window.CXQ || []).push((CX) => {
      const init = (root) => root.querySelectorAll('[data-cx-reviews]').forEach((section) => initReviews(section, CX));
      init(document);
      document.addEventListener('shopify:section:load', (event) => init(event.target));
    });
  })();
})();

/* ---------- ramp ---------- */
(() => {
  /* ---------- sections/cx-ramp.liquid: the dose schedule fills the tube one step at a time ---------- */
  (() => {
    // Share of each step's scroll spent filling to its mark; the rest holds on that step.
    const FILL = 0.45;

    class Ramp {
      constructor(section, CX) {
        this.CX = CX;
        this.section = section;
        this.track = section.querySelector('[data-cx-rp-track]');
        this.stage = section.querySelector('[data-cx-rp-stage]');
        this.steps = Array.from(section.querySelectorAll('[data-cx-rp-step]')).map((li) => ({
          li,
          value: li.dataset.value || '',
          unit: li.dataset.unit || '',
          detail: li.dataset.detail || '',
          level: (parseFloat(li.dataset.level) || 0) / 100,
        }));
        this.digits = Array.from(section.querySelectorAll('[data-cx-rp-dg]'));
        this.unit = section.querySelector('[data-cx-rp-unit]');
        this.split = section.querySelector('[data-cx-rp-split]');
        this.active = -1;
        section.classList.add('is-pinned');
        this.removeScene = CX.addScene(this);
      }

      destroy() {
        this.removeScene();
        this.section.classList.remove('is-pinned', 'is-filling');
        this.stage.style.setProperty('--cx-rp-level', String(this.steps.length ? this.steps[0].level : 1));
        this.steps.forEach((s, i) => {
          s.li.classList.toggle('is-on', i === 0);
          s.li.classList.remove('is-done');
        });
      }

      measure() {
        this.top = this.CX.docTop(this.track);
        this.H = this.stage.offsetHeight;
        this.distance = Math.max(1, this.track.offsetHeight - this.H);
        this.lastP = null;
      }

      update(view) {
        const { CX } = this;
        if (!this.steps.length) return;
        const p = CX.clamp((view.y - this.top) / this.distance, 0, 1);
        if (p === this.lastP) return;
        this.lastP = p;
        const n = this.steps.length;
        const x = p * n;
        const k = Math.min(n - 1, Math.floor(x));
        const t = CX.ease.inOut(CX.seg(x - k, 0, FILL));
        const from = k === 0 ? 0 : this.steps[k - 1].level;
        this.stage.style.setProperty('--cx-rp-level', CX.lerp(from, this.steps[k].level, t).toFixed(4));
        const onScreen = view.y + view.h > this.top && view.y < this.top + this.track.offsetHeight;
        this.section.classList.toggle('is-filling', onScreen && t > 0.02 && t < 0.97);
        if (k !== this.active) this.activate(k);
      }

      activate(k) {
        const dir = k > this.active ? 1 : -1;
        this.active = k;
        this.steps.forEach((s, i) => {
          s.li.classList.toggle('is-on', i === k);
          s.li.classList.toggle('is-done', i < k);
        });
        const s = this.steps[k];
        const pad = this.digits.length - s.value.length;
        this.digits.forEach((d, i) => this.CX.rollDigit(d, i < pad ? '' : s.value[i - pad], true, dir));
        if (this.unit) this.unit.textContent = s.unit;
        if (this.split) {
          this.split.textContent = s.detail;
          this.split.classList.remove('is-new');
          void this.split.offsetWidth;
          this.split.classList.add('is-new');
        }
      }
    }

    const instances = new Map();
    const initAll = (CX, root = document) => {
      root.querySelectorAll('[data-cx-ramp]').forEach((section) => {
        if (instances.has(section) || CX.reduced) return;
        instances.set(section, new Ramp(section, CX));
      });
    };
    const destroyAll = (root = document) => {
      instances.forEach((instance, section) => {
        if (root === document || root.contains(section)) {
          instance.destroy();
          instances.delete(section);
        }
      });
    };

    (window.CXQ = window.CXQ || []).push((CX) => {
      if (!document.querySelector('[data-cx-ramp]')) return;
      initAll(CX);
      CX.onReduceChange(() => {
        destroyAll();
        initAll(CX);
      });
      document.addEventListener('shopify:section:load', (event) => initAll(CX, event.target));
      document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
    });
  })();
})();

/* ---------- source ---------- */
(() => {
  /* ---------- sections/cx-source.liquid: a pinned photo story, one chapter at a time ---------- */
  (() => {
    // In chapter units: the next photo dissolves in over DISSOLVE before its chapter starts.
    const DISSOLVE = 0.24;

    class Source {
      constructor(section, CX) {
        this.CX = CX;
        this.section = section;
        this.track = section.querySelector('[data-cx-so-track]');
        this.stage = section.querySelector('[data-cx-so-stage]');
        this.chapters = Array.from(section.querySelectorAll('[data-cx-so-ch]')).map((li) => ({
          li,
          shot: li.querySelector('[data-cx-so-shot]'),
          copy: li.querySelector('[data-cx-so-copy]'),
        }));
        section.classList.add('is-pinned');
        section.style.setProperty('--cx-so-n', String(this.chapters.length));
        this.stopPredecode = CX.predecode(section);
        this.removeScene = CX.addScene(this);
      }

      destroy() {
        this.removeScene();
        if (this.stopPredecode) this.stopPredecode();
        this.section.classList.remove('is-pinned');
        this.chapters.forEach(({ li, shot, copy }) => {
          li.classList.remove('is-on');
          [shot, copy].forEach((el) => {
            el.style.transform = '';
            el.style.opacity = '';
          });
        });
      }

      measure() {
        this.top = this.CX.docTop(this.track);
        this.H = this.stage.offsetHeight;
        this.distance = Math.max(1, this.track.offsetHeight - this.H);
        this.lastP = null;
      }

      update(view) {
        const { CX } = this;
        const p = CX.clamp((view.y - this.top) / this.distance, 0, 1);
        if (p === this.lastP) return;
        this.lastP = p;
        const n = this.chapters.length;
        // Chapter i owns [i, i + 1) of x; the last one holds to the end.
        const x = p * (n - 0.35);
        this.chapters.forEach(({ li, shot, copy }, i) => {
          const fadeIn = i === 0 ? 1 : CX.ease.inOut(CX.seg(x, i - DISSOLVE, i + 0.06));
          const settle = CX.ease.out(CX.seg(x, i - DISSOLVE, i + 1));
          CX.set(shot, `scale(${(1.1 - 0.1 * settle).toFixed(4)})`, fadeIn);
          const last = i === n - 1;
          const cin = i === 0 ? 1 : CX.ease.out(CX.seg(x, i - 0.04, i + 0.14));
          const cout = last ? 0 : CX.ease.in(CX.seg(x, i + 0.78, i + 0.94));
          CX.set(copy, `translate3d(0, ${((1 - cin) * 1.6 - cout * 1.2).toFixed(2)}rem, 0)`, cin * (1 - cout));
          li.classList.toggle('is-on', cin > 0.5 && cout < 0.5);
        });
      }
    }

    const instances = new Map();
    const initAll = (CX, root = document) => {
      root.querySelectorAll('[data-cx-source]').forEach((section) => {
        if (instances.has(section) || CX.reduced) return;
        instances.set(section, new Source(section, CX));
      });
    };
    const destroyAll = (root = document) => {
      instances.forEach((instance, section) => {
        if (root === document || root.contains(section)) {
          instance.destroy();
          instances.delete(section);
        }
      });
    };

    (window.CXQ = window.CXQ || []).push((CX) => {
      if (!document.querySelector('[data-cx-source]')) return;
      initAll(CX);
      CX.onReduceChange(() => {
        destroyAll();
        initAll(CX);
      });
      document.addEventListener('shopify:section:load', (event) => initAll(CX, event.target));
      document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
    });
  })();
})();

/* ---------- day ---------- */
(() => {
  /* ---------- sections/cx-day.liquid: the sky, the sun and the clock move through the day ---------- */
  (() => {
    // Share of each moment's scroll spent moving the clock to it; the rest holds on that moment.
    const MOVE = 0.42;
    // Sky by minute of the day: [minute, top color, horizon color].
    const SKY = [
      [300, '#141638', '#3b3a78'],
      [390, '#5b6fd6', '#ffb38a'],
      [480, '#8fc2ff', '#ffe7b0'],
      [720, '#6fb0ff', '#e6f2ff'],
      [1020, '#7fa8f0', '#ffe0b8'],
      [1170, '#6a5fd0', '#ff9a76'],
      [1260, '#221c5c', '#5a4aa8'],
      [1350, '#0c0b26', '#231f55'],
    ];
    const DUSK = 1185;
    const DAWN = 380;

    const rgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
    const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));
    const css = (c) => `rgb(${c[0]} ${c[1]} ${c[2]})`;
    const lum = (c) => (0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]) / 255;
    const skyAt = (m) => {
      if (m <= SKY[0][0]) return [rgb(SKY[0][1]), rgb(SKY[0][2])];
      for (let i = 1; i < SKY.length; i += 1) {
        if (m <= SKY[i][0]) {
          const t = (m - SKY[i - 1][0]) / (SKY[i][0] - SKY[i - 1][0]);
          return [mix(rgb(SKY[i - 1][1]), rgb(SKY[i][1]), t), mix(rgb(SKY[i - 1][2]), rgb(SKY[i][2]), t)];
        }
      }
      const last = SKY[SKY.length - 1];
      return [rgb(last[1]), rgb(last[2])];
    };

    class Day {
      constructor(section, CX) {
        this.CX = CX;
        this.section = section;
        this.track = section.querySelector('[data-cx-dy-track]');
        this.stage = section.querySelector('[data-cx-dy-stage]');
        this.sky = section.querySelector('[data-cx-dy-sky]');
        this.sun = section.querySelector('[data-cx-dy-sun]');
        this.digits = Array.from(section.querySelectorAll('[data-cx-dy-dg]'));
        this.ampm = section.querySelector('[data-cx-dy-ampm]');
        this.start = parseFloat(section.dataset.start) || 360;
        this.end = parseFloat(section.dataset.end) || 1380;
        const dots = Array.from(section.querySelectorAll('[data-cx-dy-dot]'));
        this.moments = Array.from(section.querySelectorAll('[data-cx-dy-moment]')).map((li, i) => ({ li, dot: dots[i], min: parseFloat(li.dataset.min) || 0 }));
        this.active = -1;
        this.shown = '';
        section.classList.add('is-pinned');
        this.stopPredecode = CX.predecode(section);
        this.removeScene = CX.addScene(this);
      }

      destroy() {
        this.removeScene();
        if (this.stopPredecode) this.stopPredecode();
        this.section.classList.remove('is-pinned', 'is-night');
        this.section.dataset.tone = this.section.dataset.staticTone || this.section.dataset.tone;
        this.sky.style.background = '';
        this.moments.forEach(({ li, dot }) => [li, dot].forEach((el) => el && el.classList.remove('is-on', 'is-done')));
      }

      measure() {
        this.top = this.CX.docTop(this.track);
        this.H = this.stage.offsetHeight;
        this.distance = Math.max(1, this.track.offsetHeight - this.H);
        this.lastP = null;
      }

      update(view) {
        const { CX } = this;
        const n = this.moments.length;
        if (!n) return;
        const p = CX.clamp((view.y - this.top) / this.distance, 0, 1);
        if (p === this.lastP) return;
        const first = this.lastP === null;
        this.lastP = p;
        const x = p * n;
        const k = Math.min(n - 1, Math.floor(x));
        const from = k === 0 ? Math.min(this.start, this.moments[0].min) : this.moments[k - 1].min;
        const t = CX.ease.inOut(CX.seg(x - k, 0, MOVE));
        const minute = CX.lerp(from, this.moments[k].min, t);
        this.paint(minute, first);
        const settled = t > 0.8;
        const on = settled ? k : k - 1;
        if (on !== this.active) {
          this.active = on;
          this.moments.forEach(({ li, dot }, i) => {
            [li, dot].forEach((el) => {
              if (!el) return;
              el.classList.toggle('is-on', i === on);
              el.classList.toggle('is-done', i < on);
            });
          });
        }
      }

      paint(minute, instant) {
        const { CX } = this;
        const [top, horizon] = skyAt(minute);
        this.sky.style.background = `linear-gradient(180deg, ${css(top)} 0%, ${css(horizon)} 100%)`;
        const night = minute >= DUSK || minute < DAWN;
        const tone = lum(mix(top, horizon, 0.45)) < 0.42 ? 'light' : 'dark';
        if (tone !== this.section.dataset.tone) this.section.dataset.tone = tone;
        this.section.classList.toggle('is-night', night);
        // The sun rides the arc across the section's clock range; after dusk the same disc is the moon.
        const f = CX.clamp((minute - this.start) / Math.max(1, this.end - this.start));
        const lift = Math.sin(Math.PI * CX.clamp(f * 0.9 + 0.05));
        CX.set(this.sun, `translate3d(${(f * 100).toFixed(2)}cqw, ${((1 - lift) * 100).toFixed(2)}cqh, 0)`);
        // The clock shows 5-minute steps while it moves, the exact time once it lands.
        const m = Math.round(minute / 5) * 5;
        let hh = Math.floor(m / 60) % 24;
        const mm = m % 60;
        const ap = hh >= 12 ? 'PM' : 'AM';
        hh %= 12;
        if (hh === 0) hh = 12;
        const text = `${String(hh).padStart(2, ' ')}${String(mm).padStart(2, '0')}`;
        if (text === this.shown) return;
        this.shown = text;
        this.digits.forEach((d, i) => CX.rollDigit(d, text[i] === ' ' ? '' : text[i], !instant, 1));
        if (this.ampm) this.ampm.textContent = ap;
      }
    }

    const instances = new Map();
    const initAll = (CX, root = document) => {
      root.querySelectorAll('[data-cx-day]').forEach((section) => {
        if (instances.has(section) || CX.reduced) return;
        section.dataset.staticTone = section.dataset.tone;
        instances.set(section, new Day(section, CX));
      });
    };
    const destroyAll = (root = document) => {
      instances.forEach((instance, section) => {
        if (root === document || root.contains(section)) {
          instance.destroy();
          instances.delete(section);
        }
      });
    };

    (window.CXQ = window.CXQ || []).push((CX) => {
      if (!document.querySelector('[data-cx-day]')) return;
      initAll(CX);
      CX.onReduceChange(() => {
        destroyAll();
        initAll(CX);
      });
      document.addEventListener('shopify:section:load', (event) => initAll(CX, event.target));
      document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
    });
  })();
})();

/* ---------- collhero ---------- */
(() => {
  /* ---------- sections/cx-coll-hero.liquid: the lineup drifts apart a little as the hero scrolls away ---------- */
  (() => {
    class Lineup {
      constructor(section, CX) {
        this.CX = CX;
        this.section = section;
        this.bottles = Array.from(section.querySelectorAll('[data-cx-ch-bottle]')).map((li) => ({
          li,
          img: li.querySelector('img'),
          z: parseFloat(li.style.getPropertyValue('--cx-z')) || 0,
        }));
        this.removeScene = CX.addScene(this);
      }

      destroy() {
        this.removeScene();
        this.bottles.forEach(({ img }) => {
          img.style.transform = '';
        });
      }

      measure() {
        this.top = this.CX.docTop(this.section);
        this.H = this.section.offsetHeight;
        this.lastP = null;
      }

      update(view) {
        const p = this.CX.clamp((view.y - this.top) / Math.max(1, this.H), 0, 1);
        if (p === this.lastP) return;
        this.lastP = p;
        this.bottles.forEach(({ img, z }) => this.CX.set(img, `translate3d(0, ${(-p * z * 2.4).toFixed(2)}rem, 0)`));
      }
    }

    const instances = new Map();
    const initAll = (CX, root = document) => {
      root.querySelectorAll('[data-cx-coll-hero]').forEach((section) => {
        if (instances.has(section) || CX.reduced) return;
        instances.set(section, new Lineup(section, CX));
      });
    };

    (window.CXQ = window.CXQ || []).push((CX) => {
      if (!document.querySelector('[data-cx-coll-hero]')) return;
      initAll(CX);
      CX.onReduceChange(() => {
        instances.forEach((instance) => instance.destroy());
        instances.clear();
        initAll(CX);
      });
      document.addEventListener('shopify:section:load', (event) => initAll(CX, event.target));
    });
  })();
})();

window.CXFlush();
})();
